/* ============================================================
   28 隻小怪的戰鬥數值與攻擊載荷
   ============================================================

   造型檔（`entities/families/*.ts`）是從圖鑑檢視台 1:1 移植的，
   **刻意不含任何戰鬥數值** —— 那邊沒有血量、沒有傷害、沒有經驗值。
   這個檔補的就是缺的那一半，`data/enemies.ts` 負責把兩邊合起來。

   ## 血量怎麼訂的：軸是 `wt`（稀有度），不是登場時間

   使用者 2026-08-02 選「照新表重訂」。**越常見越脆，越稀有越硬**：

       hp = round( (77 − 37·ln wt) × 家族係數 )

   這條公式不是憑空的 —— 它是**回歸既有 SPIKE 四隻的數值**得到的
   （NADEL wt 4.2→24、FÜHLER wt 0.6→96，中間兩隻誤差 ±7%），
   所以家族係數 1.0 就是 SPIKE。這樣新的 24 隻與已經校準過的 4 隻
   在同一條線上，不會出現「新家族整批偏強」。

   家族係數是**剪影的延伸**：一面牆本來就該比一根針耐打。

       spike 1.00　wedge 1.15　wall 1.55　ladle 1.00
       orb   1.00　bar   0.95　wing 0.75　ring  1.10

   `xp` = max(1, round(hp / 24))：同樣回歸自 SPIKE（24→1、96→4）。
   `imp` = round(hp × 0.36)，**NADEL 維持 0** —— 它是密度的主體，
   精英化的 NADEL 也不該製造踉蹌噪音（`sim/impact.ts` 只讓精英與 Boss 踉蹌）。

   ## 接觸傷害只有紅色有

   規格 §2.3。橙／紫／天藍的 `dmg` 一律 0，它們的威脅走各自的攻擊。
   **這條破例一次，四色語彙就開始說謊**（`elites.test.ts` 有一條守它）。

   ## `r` 是碰撞半徑，跟造型寬度不是同一件事

   r = clamp(0.95 + 0.42 × (w − 0.62), 0.95, 1.50)。
   判定圈刻意比造型大一圈（20px 下看不出邊界），而且**不吃 `BODY_K`** ——
   體積 ×2 是視覺決定、碰撞維持舊量級是手感決定，兩者各自獨立。

   ## `y` 是彈道基準高度，不是造型高度

   `eShoot` 用 `T.y + 1.6` 當出膛高度。地面型一律 0；**浮空型要填**
   `浮空高 × BODY_K − 1.6`，否則 HORCHER 的子彈會從牠腳下兩公尺處冒出來。
   浮空的辨識靠「影子離身體很遠」（規格 §4.2），接地陰影一律畫在 y=0.02。

   ## `cap` 是物件池容量

   **池子比同屏上限小的話上限是假的**：`Pool.get()` 回 null，編隊靜默少一半，
   而且是「有時候少」（看當下的敵種分布），最難查的那種（M8 記過一次）。
   cap = clamp(round(50 + 40·wt), 70, 160)，NADEL 例外給 300 ——
   0:00–1:30 只有牠一種，那一段的整個場面都是牠。
   代價是開機時預先配置約 2.9k 個物件（`Pool` 不是延遲配置的）。 */

/** 每一隻的戰鬥數值。欄位語意見檔頭。 */
export var STATS = {
  /* ---------- SPIKE 錐（已校準，不要動） ---------- */
  nadel: { hp: 24, dmg: 8, imp: 0, xp: 1, score: 1, r: 0.95, y: 0, cap: 300 },
  lanze: { hp: 58, dmg: 0, imp: 22, xp: 2, score: 2, r: 1.00, y: 0, cap: 110 },
  geissel: { hp: 72, dmg: 0, imp: 26, xp: 3, score: 3, r: 1.10, y: 0, cap: 98 },
  fuhler: { hp: 96, dmg: 0, imp: 34, xp: 4, score: 4, r: 1.05, y: 0, cap: 74 },
  /* ---------- WEDGE 楔（×1.15） ---------- */
  keil: { hp: 59, dmg: 12, imp: 21, xp: 2, score: 2, r: 1.26, y: 0, cap: 130 },
  pflug: { hp: 85, dmg: 0, imp: 31, xp: 4, score: 4, r: 1.29, y: 0, cap: 94 },
  amboss: { hp: 89, dmg: 0, imp: 32, xp: 4, score: 4, r: 1.24, y: 0, cap: 90 },
  /* ---------- WALL 壁（×1.55，全表最耐打） ---------- */
  schild: { hp: 119, dmg: 0, imp: 43, xp: 5, score: 5, r: 1.24, y: 0, cap: 90 },
  ramme: { hp: 104, dmg: 16, imp: 37, xp: 4, score: 4, r: 1.20, y: 0, cap: 102 },
  luke: { hp: 125, dmg: 0, imp: 45, xp: 5, score: 5, r: 1.18, y: 0, cap: 86 },
  /* ---------- LADLE 勺（×1.00） ---------- */
  morser: { hp: 65, dmg: 0, imp: 23, xp: 3, score: 3, r: 1.09, y: 0, cap: 106 },
  haken: { hp: 85, dmg: 0, imp: 31, xp: 4, score: 4, r: 1.08, y: 0, cap: 82 },
  bake: { hp: 103, dmg: 0, imp: 37, xp: 4, score: 4, r: 1.05, y: 0, cap: 70 },
  /* ---------- ORB 球（×1.00；AUGE 浮空 1.24） ---------- */
  rolle: { hp: 48, dmg: 10, imp: 17, xp: 2, score: 2, r: 1.09, y: 0, cap: 138 },
  auge: { hp: 77, dmg: 0, imp: 28, xp: 3, score: 3, r: 1.07, y: 0.88, cap: 90 },
  dorn: { hp: 51, dmg: 0, imp: 18, xp: 2, score: 2, r: 1.03, y: 0, cap: 130 },
  blase: { hp: 96, dmg: 0, imp: 35, xp: 4, score: 4, r: 1.15, y: 0, cap: 74 },
  /* ---------- BAR 桿（×0.95） ---------- */
  sense: { hp: 57, dmg: 14, imp: 21, xp: 2, score: 2, r: 1.45, y: 0, cap: 114 },
  spule: { hp: 77, dmg: 0, imp: 28, xp: 3, score: 3, r: 0.99, y: 0, cap: 86 },
  harfe: { hp: 81, dmg: 0, imp: 29, xp: 3, score: 3, r: 1.33, y: 0, cap: 82 },
  /* ---------- WING 翼（×0.75，全部浮空） ---------- */
  krahe: { hp: 47, dmg: 0, imp: 17, xp: 2, score: 2, r: 1.24, y: 1.84, cap: 110 },
  falke: { hp: 48, dmg: 13, imp: 17, xp: 2, score: 2, r: 1.18, y: 1.40, cap: 106 },
  schwarm: { hp: 61, dmg: 0, imp: 22, xp: 3, score: 3, r: 1.29, y: 2.12, cap: 86 },
  horcher: { hp: 83, dmg: 0, imp: 30, xp: 3, score: 3, r: 1.37, y: 2.40, cap: 70 },
  /* ---------- RING 環（×1.10） ---------- */
  nest: { hp: 113, dmg: 0, imp: 41, xp: 5, score: 5, r: 1.30, y: 0, cap: 70 },
  reifen: { hp: 68, dmg: 11, imp: 24, xp: 3, score: 3, r: 1.19, y: 0, cap: 110 },
  kranz: { hp: 94, dmg: 0, imp: 34, xp: 4, score: 4, r: 1.25, y: 0, cap: 82 },
  wirbel: { hp: 99, dmg: 0, imp: 36, xp: 4, score: 4, r: 1.29, y: 0, cap: 78 },

  /* ---------- 深層 12 隻（規格 §2.2b） ----------

     **同一條血量公式，多乘一個深層係數 1.25。** 深層是階層（第三軸）不是家族，
     所以家族係數照舊；1.25 這個數字的來源是「它們全部登場在第 13 分鐘之後」——
     那時玩家的輸出已經是第 3 分鐘的好幾倍，同一條公式給出來的血量會讀作
     「跟雜魚一樣脆」，那樣「深層」就只是換了個造型而已。

     **1.25 是估的，不是量的。** 第一次有人實玩到 13 分鐘之後就要重看
     （`PSEED=<hex> npx vitest run src/pressure.test.ts --disableConsoleIntercept`）。 */
  spross: { hp: 84, dmg: 0, imp: 30, xp: 4, score: 4, r: 0.98, y: 0, cap: 102 },
  faden: { hp: 120, dmg: 0, imp: 43, xp: 5, score: 5, r: 0.95, y: 0, cap: 74 },
  kriecher: { hp: 64, dmg: 12, imp: 23, xp: 3, score: 3, r: 1.27, y: 0, cap: 146 },
  laich: { hp: 93, dmg: 0, imp: 33, xp: 4, score: 4, r: 1.22, y: 0, cap: 106 },
  pforte: { hp: 175, dmg: 0, imp: 63, xp: 7, score: 7, r: 1.21, y: 0, cap: 78 },
  zunge: { hp: 92, dmg: 14, imp: 33, xp: 4, score: 4, r: 1.06, y: 0, cap: 94 },
  klumpen: { hp: 69, dmg: 11, imp: 25, xp: 3, score: 3, r: 1.14, y: 0, cap: 122 },
  iris: { hp: 101, dmg: 0, imp: 36, xp: 4, score: 4, r: 1.18, y: 0, cap: 86 },
  rippe: { hp: 114, dmg: 0, imp: 41, xp: 5, score: 5, r: 1.39, y: 0, cap: 74 },
  /* 唯一的浮空深層。`y` = 浮空高 1.62 × `BODY_K` 2.0 − 1.6 = 1.64（見檔頭）。 */
  motte: { hp: 72, dmg: 0, imp: 26, xp: 3, score: 3, r: 1.33, y: 1.64, cap: 90 },
  maul: { hp: 85, dmg: 13, imp: 31, xp: 4, score: 4, r: 1.27, y: 0, cap: 110 },
  leier: { hp: 117, dmg: 0, imp: 42, xp: 5, score: 5, r: 1.24, y: 0, cap: 82 }
};

/* ============================================================
   攻擊載荷
   ============================================================

   造型表只講「什麼時候動」（tTel/tAtk/tRec），這裡講「動了會發生什麼」。
   `sim/enemy-atk.ts` 是唯一的消費者。

   **十三種 kind 跑完 28 隻**，不是一隻一個函式 —— 一隻一個的話這個檔會
   長成一個沒有人敢動的長函式，而且新家族進來時沒有可重用的東西。
   分類軸是「玩家要做的反應」，不是「牠長什麼樣」：

     lunge  撲過來        → 持續走位
     spin   近身橫掃      → 拉開（刃是牠身體的一部分，仍然是接觸傷害）
     bolt   直線彈道      → 側移／躲掩體
     sweep  掃射一排      → 反著掃的方向跑
     radial 環狀齊射      → 貼近或遠離，不要停在中距
     mortar 曲射落點      → 離開圈
     jump   跳到你頭上    → 離開圈
     fuse   自爆          → 拉開距離，別補刀
     pull   把你拉過去    → 先打斷它
     mark   增傷友軍      → 先打它
     guard  減傷友軍      → 先打它
     summon 生小怪        → 先打它

   四色語彙的對照（規格 §2.3）：
     紅  lunge / spin（接觸）　橙  bolt / sweep / radial / pull（直線）
     紫  mortar / jump / fuse（範圍預警）　天藍  mark / guard / summon（支援）

   ⚠️ **紅色的攻擊一律不加獨立傷害來源。** lunge 只給速度、spin 只暫時放大
   接觸判定半徑，痛的都是撞到 —— 紅色多一條彈道判定，玩家學到的
   「紅＝碰到才痛」就是錯的，而他會拿那個錯的認知去打其餘 27 種。 */
export var ATK = {
  /* ---------- SPIKE 錐（已校準，不要動） ---------- */
  nadel: { kind: 'lunge', push: 2.4 },
  lanze: { kind: 'bolt', dmg: 11, speed: 19 },
  geissel: { kind: 'cone', dmg: 9, speed: 15, n: 5, spread: 0.42 },
  fuhler: { kind: 'mark', radius: 9.5, dmg: 1.35, spd: 1.18, dur: 4.5 },
  /* ---------- WEDGE 楔 ---------- */
  keil: { kind: 'lunge', push: 2.8 },
  /* 掃射：五發**分散在整個攻擊窗口裡**依序打出，角度從 −arc 掃到 +arc。
     一次全射出去就退化成 GEISSEL 的扇形了 —— 掃射的對策是
     「往掃過來的反方向跑」，那需要它真的有掃的時間差。 */
  pflug: { kind: 'sweep', dmg: 8, speed: 20, n: 5, arc: 0.55 },
  /* 跳擊：落點在預備開始那一刻就鎖定（`e.tgx/tgz`），所以預警圈是誠實的。
     鎖定當下的玩家位置而不是落地時的 —— 後者等於「圈只是裝飾」。 */
  amboss: { kind: 'jump', dmg: 20, radius: 5.0, push: 3.4 },
  /* ---------- WALL 壁 ---------- */
  ramme: { kind: 'lunge', push: 3.0 },
  /* 三連點放。`gap` 走 `schedule()`（吃模擬步的 dt），不是牆鐘計時器。 */
  luke: { kind: 'bolt', dmg: 10, speed: 21, burst: 3, gap: 0.13 },
  /* 護盾：**只給友軍減傷，自己一點傷害都沒有**。半徑小但倍率高 ——
     SCHILD 的定位是「擋線」，玩家要學的是繞後而不是多打幾發。 */
  schild: { kind: 'guard', radius: 7.0, arm: 1.70, dur: 5.0 },
  /* ---------- LADLE 勺 ---------- */
  /* 牽引：把玩家拉向自己。**不造成傷害** —— 它的威脅是把你拖進別人的射程。
     會被地形擋（橙的通則），所以躲掩體是有效解。 */
  haken: { kind: 'pull', force: 17, dur: 0.9 },
  morser: { kind: 'mortar', dmg: 16, radius: 3.4, n: 1, spread: 0, delay: 0.9 },
  bake: { kind: 'mark', radius: 10.0, dmg: 1.25, spd: 1.10, dur: 5.0 },
  /* ---------- ORB 球 ---------- */
  rolle: { kind: 'lunge', push: 2.6 },
  auge: { kind: 'bolt', dmg: 14, speed: 26 },
  /* 自爆：`boom` 由 `sim/enemy-kinds.ts` 的 `detonate` 讀。
     **打死它一樣會炸**（死亡殉爆），所以正解是拉開距離不是多補兩發。 */
  dorn: { kind: 'fuse', boom: { r: 5.0, dmg: 26 } },
  blase: { kind: 'guard', radius: 11.0, arm: 1.40, dur: 6.0 },
  /* ---------- BAR 桿 ---------- */
  /* 旋掃：攻擊期間**接觸判定半徑暫時放大**（橫刃是牠身體的延伸），
     沒有另開傷害來源。造型的刃已經在轉 ×13 了，判定跟著它走才誠實。 */
  sense: { kind: 'spin', reach: 2.6 },
  spule: { kind: 'bolt', dmg: 20, speed: 34 },
  harfe: { kind: 'mortar', dmg: 12, radius: 2.8, n: 3, spread: 3.2, delay: 0.8 },
  /* ---------- WING 翼 ---------- */
  krahe: { kind: 'bolt', dmg: 10, speed: 22 },
  falke: { kind: 'lunge', push: 3.4 },
  schwarm: { kind: 'mortar', dmg: 14, radius: 3.0, n: 2, spread: 2.6, delay: 0.7 },
  horcher: { kind: 'mark', radius: 12.0, dmg: 1.30, spd: 1.12, dur: 5.0 },
  /* ---------- RING 環 ---------- */
  /* 召喚：**只生得出已經介紹過的東西**（`sim/enemy-atk.ts` 會過濾），
     否則它會在第 16 分鐘變成「第一次見面」的場合，違反支柱一。 */
  nest: { kind: 'summon', n: 3, dist: 4.5 },
  reifen: { kind: 'lunge', push: 2.8 },
  /* 環射：七發徑向打出去。對策是**貼近或拉遠，不要停在中距** ——
     所以預警是自己腳下的一個圈，不是七條線（七條線是噪音不是資訊）。 */
  kranz: { kind: 'radial', dmg: 9, speed: 18, n: 7 },
  /* 旋渦：範圍牽引 + 收束時的一次傷害。紫色，所以有地面預警圈。 */
  wirbel: { kind: 'pull', force: 13, dur: 1.1, area: true, dmg: 10 },

  /* ══════════════════════════════════════════════════════════════
     深層 12 隻（規格 §2.2b）
     ══════════════════════════════════════════════════════════════

     **沒有新的 kind。** 深層是疊在八個家族上的第三軸，不是第九個家族 ——
     牠們的「不對勁」全部由造型講（一個不受光的洞 + 左右不對稱），
     行為仍然照四色語彙走。多發明一種反應方式等於在第 13 分鐘
     叫玩家重學一次規則，而那正是深層想避免的事。 */
  spross: { kind: 'jump', dmg: 14, radius: 3.2, push: 2.4 },
  faden: { kind: 'mark', radius: 8.5, dmg: 1.28, spd: 1.14, dur: 4.5 },
  kriecher: { kind: 'lunge', push: 3.0 },
  laich: { kind: 'fuse', boom: { r: 4.4, dmg: 22 } },
  pforte: { kind: 'mortar', dmg: 18, radius: 3.8, n: 1, spread: 0, delay: 1.0 },
  /* 舌是牠身體的延伸，所以是**接觸判定放大**不是另一條彈道（紅色的通則）。 */
  zunge: { kind: 'spin', reach: 3.0 },
  klumpen: { kind: 'lunge', push: 2.6 },
  iris: { kind: 'mortar', dmg: 15, radius: 3.0, n: 1, spread: 0, delay: 0.85 },
  rippe: { kind: 'guard', radius: 8.0, arm: 1.55, dur: 5.5 },
  motte: { kind: 'mortar', dmg: 12, radius: 2.6, n: 2, spread: 2.4, delay: 0.65 },
  maul: { kind: 'spin', reach: 2.2 },
  leier: { kind: 'bolt', dmg: 12, speed: 24, burst: 3, gap: 0.11 }
};
